/**
 * Session state machine (implementation-plan.md §1.1):
 *
 *   idle → recording → transcribing → structuring → review → rendering → complete
 *
 * This module owns the LEGAL transitions and the audit log. It does not own the work behind
 * each state: transcription (callout-transcribe) and structuring (callout-structure) are the
 * PIPELINE card's, rendering is the RENDER card's. Here they are advanced by the pipeline
 * driver, so the machine is complete and testable while those workers are still stubs.
 */
import type { SessionStatus } from './store';

export const SESSION_ORDER: SessionStatus[] = [
  'idle',
  'recording',
  'transcribing',
  'structuring',
  'review',
  'rendering',
  'complete',
];

const ALLOWED: Record<SessionStatus, SessionStatus[]> = {
  idle: ['loading', 'recording', 'error'],
  loading: ['idle', 'recording', 'error'],
  recording: ['transcribing', 'idle', 'error'],
  transcribing: ['structuring', 'error', 'review'],
  structuring: ['review', 'error'],
  review: ['rendering', 'recording', 'error'],
  rendering: ['complete', 'error'],
  complete: ['idle', 'review'],
  error: ['idle', 'recording', 'transcribing', 'structuring', 'review', 'rendering'],
};

export function canTransition(from: SessionStatus, to: SessionStatus): boolean {
  return from === to || (ALLOWED[from]?.includes(to) ?? false);
}

export class IllegalTransitionError extends Error {
  constructor(
    readonly from: SessionStatus,
    readonly to: SessionStatus,
  ) {
    super(`Illegal session transition: ${from} → ${to}`);
    this.name = 'IllegalTransitionError';
  }
}

export interface TransitionEvent {
  from: SessionStatus;
  to: SessionStatus;
  at: string;
  reason: string;
}

export class SessionMachine {
  private status: SessionStatus;
  private readonly log: TransitionEvent[] = [];
  private readonly onTransition: (event: TransitionEvent) => void;

  constructor(initial: SessionStatus = 'idle', onTransition: (event: TransitionEvent) => void = () => {}) {
    this.status = initial;
    this.onTransition = onTransition;
  }

  get current(): SessionStatus {
    return this.status;
  }

  get history(): readonly TransitionEvent[] {
    return this.log;
  }

  /** Throws IllegalTransitionError on an illegal edge — never silently jumps state. */
  transition(to: SessionStatus, reason = ''): TransitionEvent {
    if (!canTransition(this.status, to)) throw new IllegalTransitionError(this.status, to);
    const event: TransitionEvent = {
      from: this.status,
      to,
      at: new Date().toISOString(),
      reason,
    };
    this.status = to;
    this.log.push(event);
    this.onTransition(event);
    return event;
  }

  /** next() walks the happy path one step; used by the pipeline driver. */
  next(reason = ''): TransitionEvent {
    const index = SESSION_ORDER.indexOf(this.status);
    if (index < 0 || index === SESSION_ORDER.length - 1) {
      throw new IllegalTransitionError(this.status, this.status);
    }
    return this.transition(SESSION_ORDER[index + 1]!, reason);
  }
}
